import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { Settings } from "../../CustomComponents/Settings";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { CustomUtils } from "../../Utils/CustomUtils";
import { Scaling } from "../../Utils/Scaling";
import { WinAnimatorExtendedFS } from "../../CustomComponents/WinAnimatorExtendedFS";
import { WheelElement } from "../../CustomComponents/WheelElement";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";

type IWin = Communication.Game.IWin;
var GameModules = Communication.Game.GameModules;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;
    static INTRO_DELAY: number = 60;
    static OUTRO_DELAY: number = 60;
    static OUTRO_DELAY_TO_GAMBLE: number = 20;
    static OUTRO_DELAY_TO_GAMBLE_IN_HIGHWINS: number = 135;
    static INTRO_DELAY_FROM_GAMBLE: number = 30;
    static OUTRO_DELAY_TO_FREESPINS: number = 90;
    static INTRO_DELAY_FROM_FREESPINS: number = 30;
    static OUTRO_DELAY_IN_HIGHWINS: number = 90;
    static OUTRO_DELAY_TO_PICK: number = 40;

    private _respin: boolean = false;

    private _controller: BaseController;
    private _wasFinished: boolean = false;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _machineStoppedDelayTimer: FrameTimerEventEmitter;
    private _mysteryDelayTimer: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;
    private _groupTweens: SwitchElement;

    private _machineStopped = true;

    private modeSetting: { Landscape?: Node; Portrait?: Node; Extended?: Node } = {};

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._machineStoppedDelayTimer = CreateFrameTimer("machineStoppedDelayTimer");
        this._machineStoppedDelayTimer.on(FrameTimerEvents.Complete, this.machineStoppedDelayed.bind(this));

        this._mysteryDelayTimer = CreateFrameTimer("mysteryDelayTimer");
        this._mysteryDelayTimer.on(FrameTimerEvents.Complete, this.mysterySymbol.bind(this));

        StageController.baseStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "GroupTweens" && this.childs[key] instanceof SwitchElement) {
                    this._groupTweens = this.childs[key]; // SwitchElement with 2 elements of GroupTweens
                }
            }

            // set childs to GroupTweens object
            if (this._groupTweens) {
                let groupTweenElements: Object = this._groupTweens.getChildElements();
                if (groupTweenElements) {
                    for (let key in groupTweenElements) {
                        if (groupTweenElements.hasOwnProperty(key)) {
                            if (groupTweenElements[key] instanceof GroupTweens) {
                                (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                            }
                        }
                    }
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        this.visible = false;
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.on(MachineExtended.SpinReelRollFinished, this.onSpinReelRollFinished.bind(this));
                        this._machine.on(MachineExtended.RespinReelStopped, this.onRespinReelStopped.bind(this));
                        this._machine.on(MachineExtended.RespinRollFinished, this.respinRollFinished.bind(this));
                        this._machine.on(MachineExtended.RespinTeaseStart, this.respinTeaseStart.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                            this.childs["TeasingBlack"],
                            this.childs["NoTrimContainer"],
                            this.childs["NoTrimTeasingContainer"],
                            this.childs["ReelTopContainer"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimatorExtended.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimatorExtended.LimitWinCounter, this.limitWinCounter.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                <ContainerElement>this._winContainer.getChildElementByName("NestedWinContainer"),
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels, true);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;

        if (ModuleManager.gambleActive()) {
            this.visible = true;

            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleInit);
            }
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;
        StageController.pickStage = false;

        if (GameManager.gameResponse.totalWin > 0) {
            if (ModuleManager.freespinsEnabled()) {
                //init state before fs
                PlayList.loop("SND_BaseLoop");
            } else if (GameManager.baseResponse.currentWin == 0) {
                //init state after fs
                PlayList.loop("SND_BaseRecapLoop");
            }
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        this._winAnimator.showInitWins(
            GameManager.baseResponse.wins,
            GameManager.baseResponse.currentWin,
            GameManager.baseResponse.totalWin,
            0,
        );
        this._winAnimator.recountingWaysCounter(-1); // -1 get from reels

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseInitIntro);
        }

        this.visible = true;

        if (Settings.isRespin()) {
            AmbientBackground.changeAmbientBackground("color-1", 100);
        }
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.reset(false, false, false, true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        this._respin = false;

        this._machineStopped = false;

        this._machine.spinStart(this._winAnimator.reset(false, true, false), false);
        this._winAnimator.removeAllCurrentSymbol();
    }

    stop(): void {
        if (this._machineStopped) {
            if (Settings.isRespin()) {
                this._controller.winCalculationFinished(); // for show win
            } else {
                this._winAnimator.reset(true);
            }
        } else {
            this._machine.spinStop(null, true);
        }
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    onRespinReelStopped(): void {
        this._winAnimator.onRespinReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    onSpinReelRollFinished(reelIndex: number): void {
        this._winAnimator.onSpinReelRollFinished(GameManager.baseResponse.screen.reels, reelIndex);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(respin: boolean = false): void {
        this._respin = respin;

        // show symbols after is respin completed whole, but normal spin symbols is showed in onReelStopped
        if (this._respin) {
            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
        }

        if (this._winAnimator.hasMystery()) {
            this._controller.beforeMachineStopped();
            this._mysteryDelayTimer.start(WinAnimatorExtended.MysterySymbolStartDelay);
            return;
        }

        this.machineStoppedDelayed();
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    mysterySymbol(): void {
        this._machineStoppedDelayTimer.stop();
        this._winAnimator.mysterySymbols();
        this._machineStoppedDelayTimer.start(WinAnimatorExtended.MysterySymbolAnimDelay);
    }

    machineStoppedDelayed(): void {
        this._wasFinished = false;
        this._machineStopped = true;
        this._controller.machineStopped(this._respin);
        this._winAnimator.machineStopped();

        if (
            this._winAnimator.showWins(
                GameManager.baseResponse.wins,
                GameManager.baseResponse.currentWin,
                GameManager.baseResponse.totalWin,
                0,
                this._respin,
            )
        ) {
            // in HW
            PlayList.setVolume("SND_BaseLoop", 0, 5);
            this._controller.highWins(); // for enable stop button
        }
    }

    limitWinCounter(isHighWin: boolean = false): void {
        if (isHighWin) {
            // in HW
            PlayList.setVolume("SND_BaseLoop", 0, 5);
            this._controller.highWins(); // for enable stop button
        }
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            if (!Settings.isRespin() && GameManager.gameResponse.totalWin > 0 && !ModuleManager.pickEnabled()) {
                PlayList.setVolume("SND_BaseLoop", 0, 5);
                PlayList.loop("SND_BaseRecapLoop");
            }
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true, false, false, true);
    }

    intro(long: boolean = false, previousModule: string): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;
        StageController.pickStage = false;

        this.visible = true;

        if (GameManager.gameResponse.totalWin > 0) {
            //fs return
            PlayList.setVolume("SND_BaseLoop", 0, 5);
            PlayList.loop("SND_BaseRecapLoop");

            this._winAnimator.reset(true);
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");

            this._winAnimator.reset(false, false, false, true);
        }

        let delayIntro: number = BaseView.INTRO_DELAY;
        if (previousModule && this._groupTweens) {
            if (previousModule === GameModules.Gamble) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleToBase);
                delayIntro = BaseView.INTRO_DELAY_FROM_GAMBLE;
            } else if (previousModule === GameModules.Freespins) {
                this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBase);
                delayIntro = BaseView.INTRO_DELAY_FROM_FREESPINS;
            } else {
                this._groupTweens.goToCase(GroupTweens.CaseIntro);
            }
        }

        this._winAnimator.recountingWaysCounter(-1); // -1 get from reels
        this._winAnimator.removeTrimSymbols();

        this._introDelay.start(delayIntro);
    }

    introFinished(): void {
        this._controller.introFinished();
    }

    outro(): void {
        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(false);

        PlayList.setVolume("SND_BaseLoop", 0, 5);
        PlayList.stop("SND_BaseRecapLoop", 5);

        if (ModuleManager.gambleActive()) {
            if (inHighWins && !wasFinished) {
                if (this._groupTweens) {
                    this._groupTweens.goToCase(GroupTweens.CaseBaseToGambleHighWins);
                }
                this._outroDelay.start(BaseView.OUTRO_DELAY_TO_GAMBLE_IN_HIGHWINS);
            } else {
                if (this._groupTweens) {
                    this._groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
                }
                this._outroDelay.start(BaseView.OUTRO_DELAY_TO_GAMBLE);
            }
        } else if (ModuleManager.freespinsActive()) {
            //  changed ambient background transition timing when switching to FS, moved from outroFinished()
            //   AmbientBackground.changeAmbientBackground('color-1', 300, 60);

            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToFreespins);
            }

            this._outroDelay.start(BaseView.OUTRO_DELAY_TO_FREESPINS);
        } else if (ModuleManager.pickEnabled()) {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToPickIN);
            }

            this._outroDelay.start(BaseView.OUTRO_DELAY_TO_PICK);
        } else {
            if (wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(BaseView.OUTRO_DELAY_IN_HIGHWINS);
                } else {
                    this._outroDelay.start(BaseView.OUTRO_DELAY);
                }
            }
        }
    }

    outroFinished(): void {
        this._outroDelay.stop();

        if (ModuleManager.freespinsActive()) {
            AmbientBackground.changeAmbientBackground("color-1", 100);
        }

        if (ModuleManager.gambleActive()) {
            // if (this._groupTweens) {
            //     this._groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
            // }
        } else if (ModuleManager.pickEnabled()) {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToPickOUT);
            }
        }

        this._controller.outroFinished();
    }

    /* RESPIN */

    respinTeaseStart(): void {
        this._controller.respinTeaseStart();
    }

    respin(): void {
        this._machineStopped = false;

        this._machine.respinStart(
            this._winAnimator.reset(false, false, true),
            true,
            CustomUtils.getCurrentSymbolsCases(this._winAnimator.currentSymbols),
        );
        this._winAnimator.removeAllCurrentSymbol();
    }

    respinRollFinished(): void {
        this._controller.respinRollFinished();
    }

    disableBase(): void {
        // this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.setVolume("SND_BaseLoop", 0, 5);
            PlayList.stop("SND_BaseRecaploop");
        }
    }

    private setMode = function (mode: "landscape" | "portrait" | "extended") {
        switch (mode) {
            case "extended":
                if (this.modeSetting.Extended) {
                    this.updateByNode(this.modeSetting.Extended);
                    break;
                }
            case "portrait":
                if (this.modeSetting.Portrait) {
                    this.updateByNode(this.modeSetting.Portrait);
                    break;
                }
            case "landscape":
                if (this.modeSetting.Landscape) {
                    this.updateByNode(this.modeSetting.Landscape);
                }
                break;
        }
    };
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/